// Typ: FindCircumradiusEquilateralTask
// Temat: Okręgi w trójkącie → Okrąg opisany w trójkącie równobocznym
// Wzór:  R = (a√3) / 3

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// ===== helpers =====
function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// ===== generator =====
export function generateTask() {
  const a = Math.floor(Math.random() * 8) + 3; // bok 3..10
  return {
    type: "FindCircumradiusEquilateralTask",
    data: { a }
  };
}

// ===== render task =====
export function renderTask({ data }) {
  const { a } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dany jest trójkąt równoboczny o boku
      <InlineMath math={`\\ a=${a}`} />. Oblicz promień okręgu opisanego{" "}
      <span style={{ whiteSpace: "nowrap" }}>
        <InlineMath math={"R"} />.
      </span>
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={"R"}
        placeholder="np. (6√3)/3, 2√3, 3.46"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { a } = data;
  const expected = (a * Math.sqrt(3)) / 3;

  // 1) standardowy parse z RootFractionInput
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: liczba dziesiętna (obsługa przecinka)
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;

  // akceptujemy także zapis dziesiętny z dokładnością do 0.01
  return Math.abs(parsed - expected) <= 0.01;
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { a } = data;
  const g = gcd(a, 3);
  const n = a / g;
  const d = 3 / g;

  let latex;
  if (d === 1) {
    // jeśli współczynnik = 1, pokaż po prostu √3
    const coeff = n === 1 ? "" : `${n} `;
    latex = `R = ${coeff}\\sqrt{3}`;
  } else {
    latex = `R = \\dfrac{${n} \\sqrt{3}}{${d}}`;
  }

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={latex} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a } = data;

  const g = gcd(a, 3);
  const n = a / g;
  const d = 3 / g;
  const canSimplify = g > 1;

  const simplifiedLatex =
    d === 1
      ? (n === 1 ? `R = \\sqrt{3}` : `R = ${n} \\sqrt{3}`)
      : `R = \\dfrac{${n} \\sqrt{3}}{${d}}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> W trójkącie równobocznym znamy wzór:</div>
      <BlockMath math={"R = \\dfrac{a\\sqrt{3}}{3}"} />

      <div><strong>Krok 2.</strong> Podstawiamy długość boku:</div>
      <BlockMath math={`R = \\dfrac{${a} \\sqrt{3}}{3}`} />

      {canSimplify && (
        <>
          <div><strong>Krok 3.</strong> Upraszczamy ułamek:</div>
          <BlockMath math={simplifiedLatex} />
        </>
      )}
    </div>
  );
}


// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, "R")} />
    </div>
  );
}
